import { CircleAlertIcon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

type CallErrorAlertProps = { title: string; description: string; className?: string };

/**
 * The destructive alert for a failed `tools/call`, used both by the page (a failed list/call) and
 * by the Test tab (a failed submission), so the two read as the same thing.
 *
 * It carries three local overrides of `ui/alert`, kept here rather than in the shared variant so
 * they don't repaint the app's other 28 alert call sites:
 *
 * - `[&>svg]:translate-y-0` cancels the shared 2px nudge. `AlertTitle` is `text-base leading-none`,
 *   so its line box is exactly 16px — the same height as the `size-4` icon — and `items-start`
 *   already lines the two up. The nudge pushes the icon 2px below the title.
 * - `gap-y-1` sets the title-to-description gap to the 4px the design asks for, up from 2px.
 * - `tracking-normal` drops the shared title's `tracking-tight`.
 */
const CallErrorAlert = ({ title, description, className }: CallErrorAlertProps) => (
  <Alert variant="destructive" className={cn('gap-y-1 [&>svg]:translate-y-0', className)}>
    <CircleAlertIcon />
    <AlertTitle className="tracking-normal">{title}</AlertTitle>
    <AlertDescription className="min-w-0 leading-6 wrap-break-word">{description}</AlertDescription>
  </Alert>
);

export default CallErrorAlert;
